import { useState } from "react";
import { Button } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// AUD-EDIT-01 (ver context/67_AUD_EDIT_01_COMENTARIO_GENERAL_VERSIONADO.md) --
// comentario general de la auditoría, EDITABLE e independiente del cierre.
//
// No confundir con el "motivo"/"justificación" de una corrección de sección
// (eso es lo que se opina de UN CAMBIO puntual); esto es lo que el auditor
// opina de la atención EN CONJUNTO, y sobrevive aunque la auditoría ya esté
// AUDITADA -- cada guardado agrega una versión nueva, ninguna se pierde.
//
// 100% independiente del modal de cierre (`AuditoriaAtencionDetalle.js`
// sigue permitiendo escribir un comentario ahí también -- ese camino ahora
// también queda historizado en backend, ver FAuditClinicalReview.ts).

// AUD-EDIT-QA-03: mismo tope que el backend (5000). Un comentario de auditoría
// real ocupa varios párrafos -- paciente, fecha, especialidad, qué se revisó y
// qué se concluyó -- y con 1000 no entraba.
const COMENTARIO_MAX = 5000;

function VersionComentario({ version }) {
    return (
        <div className="ac-comentario-general__version">
            <p className="ac-comentario-general__version-texto">{version.comentario}</p>
            <span className="ac-correccion__firma">
                {version.operation === "CREATED" ? "Creado" : "Editado"} por {version.actor?.full_name || "Auditor"}
                {version.created_at ? ` · ${format(new Date(version.created_at), "dd/MM/yyyy HH:mm", { locale: es })}` : ""}
            </span>
        </div>
    );
}

function AtencionComentarioGeneral({ actual, historial, puedeEditar, guardando, error, onGuardar, obligatorioParaCerrar }) {
    const [editando, setEditando] = useState(false);
    const [texto, setTexto] = useState("");
    const [historialAbierto, setHistorialAbierto] = useState(false);

    const versionesAnteriores = (historial || []).filter((v) => v.id !== actual?.id);

    const abrirEdicion = () => {
        setTexto(actual?.comentario || "");
        setEditando(true);
    };

    const cancelar = () => setEditando(false);

    const guardar = async () => {
        const ok = await onGuardar(texto);
        if (ok) setEditando(false);
    };

    return (
        <div className="ac-panel ac-comentario-general" data-testid="ac-comentario-general">
            <span className="ac-panel__titulo">Comentario general de la auditoría</span>

            {!editando && (
                <>
                    {actual ? (
                        <VersionComentario version={actual} />
                    ) : (
                        <span className="ac-progreso__texto" data-testid="ac-comentario-general-vacio">
                            Todavía no hay comentario sobre esta atención.
                            {obligatorioParaCerrar ? " Hace falta para poder marcarla como auditada." : ""}
                        </span>
                    )}
                    {puedeEditar && (
                        <Button size="small" onClick={abrirEdicion} data-testid="ac-comentario-general-btn">
                            {actual ? "Editar comentario" : "Agregar comentario"}
                        </Button>
                    )}
                    {versionesAnteriores.length > 0 && (
                        <Button
                            type="link"
                            size="small"
                            className="p-0 text-start"
                            onClick={() => setHistorialAbierto((v) => !v)}
                            data-testid="ac-comentario-general-toggle-historial"
                        >
                            {historialAbierto ? "Ocultar" : "Ver"} versiones anteriores ({versionesAnteriores.length})
                        </Button>
                    )}
                    {historialAbierto && (
                        <div className="ac-comentario-general__historial">
                            {versionesAnteriores.map((v) => (
                                <VersionComentario key={v.id} version={v} />
                            ))}
                        </div>
                    )}
                </>
            )}

            {editando && (
                <label className="ac-comentario-general__campo">
                    <textarea
                        rows={10}
                        value={texto}
                        maxLength={COMENTARIO_MAX}
                        placeholder={"Ej.: «En la historia clínica del paciente … atendido el … por la especialidad de … no se encontraron observaciones.\nHistoria clínica se relaciona correctamente con diagnósticos CIE10.»"}
                        onChange={(e) => setTexto(e.target.value)}
                        data-testid="ac-comentario-general-textarea"
                        autoFocus
                    />
                    <span className="ac-contador">
                        {texto.trim().length} / {COMENTARIO_MAX}
                    </span>
                    {error && (
                        <span className="ac-comentario-general__error" data-testid="ac-comentario-general-error">
                            {error}
                        </span>
                    )}
                    <div className="ac-comentario-general__acciones">
                        <Button size="small" onClick={cancelar} disabled={guardando}>
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            size="small"
                            onClick={guardar}
                            loading={guardando}
                            disabled={texto.trim().length === 0}
                            data-testid="ac-comentario-general-guardar"
                        >
                            Guardar
                        </Button>
                    </div>
                </label>
            )}
        </div>
    );
}

export default AtencionComentarioGeneral;
